import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import AuthService from "../services/AuthService";

const PasswordChangeForm = ({ cognitoUser, onPasswordChanged, onCancel }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      setError(t("loginPage.errors.passwordMismatch"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await new Promise((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(
          passwords.newPassword,
          {},
          {
            onSuccess: (result) => {
              const idToken = result.getIdToken().getJwtToken();
              const accessToken = result.getAccessToken().getJwtToken();
              const refreshToken = result.getRefreshToken().getToken();
              localStorage.setItem("idToken", idToken);
              localStorage.setItem("accessToken", accessToken);
              localStorage.setItem("refreshToken", refreshToken);
              resolve(result);
              onPasswordChanged();
            },
            onFailure: (err) => reject(err),
          },
        );
      });
    } catch (err) {
      setError(err.message || t("loginPage.errors.passwordChangeFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-[100dvh] ${colors.bgSecondary} flex flex-col items-center justify-center p-4 overflow-y-auto`}
    >
      <div
        className={`max-w-md w-full ${colors.cardBg} rounded-2xl shadow-xl p-6 sm:p-8 border ${colors.border} my-auto`}
      >
        <h2
          className={`text-2xl font-bold text-center ${colors.textPrimary} mb-4`}
        >
          {t("loginPage.passwordChange.title")}
        </h2>
        <p className={`text-center ${colors.textMuted} mb-6`}>
          {t("loginPage.passwordChange.message")}
        </p>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-100 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}
          <div className="relative">
            <Lock
              className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted}`}
            />
            <input
              type="password"
              name="newPassword"
              value={passwords.newPassword}
              onChange={handlePasswordChange}
              placeholder={t("loginPage.passwordChange.newPasswordPlaceholder")}
              className={`w-full pl-12 pr-4 py-3 rounded-lg border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>
          <div className="relative">
            <Lock
              className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted}`}
            />
            <input
              type="password"
              name="confirmPassword"
              value={passwords.confirmPassword}
              onChange={handlePasswordChange}
              placeholder={t(
                "loginPage.passwordChange.confirmPasswordPlaceholder",
              )}
              className={`w-full pl-12 pr-4 py-3 rounded-lg border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-primary-400"
          >
            {loading
              ? t("loginPage.passwordChange.changingPassword")
              : t("loginPage.passwordChange.changePassword")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className={`w-full ${colors.textMuted} py-2 rounded-lg ${colors.bgHover} transition-colors`}
          >
            {t("loginPage.passwordChange.cancel")}
          </button>
        </form>
      </div>
    </div>
  );
};

const ForgotPasswordForm = ({ onCodeSent, onCancel, authService }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError(t("loginPage.errors.enterCredentials"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await authService.forgotPassword(email);
      onCodeSent(email);
    } catch (err) {
      setError(err.message || t("loginPage.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <h2 className={`text-3xl font-bold ${colors.textPrimary} mb-2`}>
        {t("loginPage.forgotPasswordForm.title")}
      </h2>
      <p className={`${colors.textMuted} mb-8`}>
        {t("loginPage.forgotPasswordForm.message")}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="reset-email"
            className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
          >
            {t("loginPage.emailLabel")}
          </label>
          <div className="relative">
            <Mail
              className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted}`}
            />
            <input
              id="reset-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-lg border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              placeholder={t("loginPage.emailPlaceholder")}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-100 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-primary-400"
        >
          {loading
            ? t("loginPage.forgotPasswordForm.sendingCode")
            : t("loginPage.forgotPasswordForm.sendResetCode")}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className={`w-full ${colors.textMuted} py-2 rounded-lg ${colors.bgHover} transition-colors`}
        >
          {t("loginPage.forgotPasswordForm.backToLogin")}
        </button>
      </form>
    </div>
  );
};

const ResetPasswordForm = ({
  email,
  onResetSuccess,
  onCancel,
  authService,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t("loginPage.errors.passwordMismatch"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await authService.confirmPassword(
        email,
        formData.code,
        formData.newPassword,
      );
      onResetSuccess();
    } catch (err) {
      setError(err.message || t("loginPage.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <h2 className={`text-3xl font-bold ${colors.textPrimary} mb-2`}>
        {t("loginPage.forgotPasswordForm.resetPasswordTitle")}
      </h2>
      <p className={`${colors.textMuted} mb-8`}>
        {t("loginPage.forgotPasswordForm.resetPasswordMessage")}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
          >
            {t("loginPage.forgotPasswordForm.codeLabel")}
          </label>
          <input
            name="code"
            type="text"
            required
            value={formData.code}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            placeholder={t("loginPage.forgotPasswordForm.codePlaceholder")}
          />
        </div>

        <div>
          <label
            className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
          >
            {t("loginPage.forgotPasswordForm.newPasswordLabel")}
          </label>
          <div className="relative">
            <Lock
              className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted}`}
            />
            <input
              name="newPassword"
              type={showPassword ? "text" : "password"}
              required
              value={formData.newPassword}
              onChange={handleChange}
              className={`w-full pl-12 pr-12 py-3 rounded-lg border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              placeholder={t(
                "loginPage.forgotPasswordForm.newPasswordPlaceholder",
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute right-4 top-1/2 -translate-y-1/2 ${colors.textMuted}`}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
          >
            {t("loginPage.forgotPasswordForm.confirmPasswordLabel")}
          </label>
          <div className="relative">
            <Lock
              className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted}`}
            />
            <input
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full pl-12 pr-12 py-3 rounded-lg border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              placeholder={t(
                "loginPage.forgotPasswordForm.confirmPasswordPlaceholder",
              )}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-100 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-primary-400"
        >
          {loading
            ? t("loginPage.forgotPasswordForm.resettingPassword")
            : t("loginPage.forgotPasswordForm.resetPassword")}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className={`w-full ${colors.textMuted} py-2 rounded-lg ${colors.bgHover} transition-colors`}
        >
          {t("loginPage.forgotPasswordForm.backToLogin")}
        </button>
      </form>
    </div>
  );
};



const LoginPage = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [view, setView] = useState("login"); // 'login', 'forgotPassword', 'resetPassword'
  const [resetEmail, setResetEmail] = useState("");

  const userPool = new CognitoUserPool({
    UserPoolId: process.env.REACT_APP_USER_POOL_ID,
    ClientId: process.env.REACT_APP_CLIENT_ID,
  });

  const authService = new AuthService(userPool);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError(t("loginPage.errors.enterCredentials"));
      return;
    }
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const authDetails = new AuthenticationDetails({
        Username: formData.email,
        Password: formData.password,
      });
      const cognitoUserInstance = new CognitoUser({
        Username: formData.email,
        Pool: userPool,
      });

      await new Promise((resolve, reject) => {
        cognitoUserInstance.authenticateUser(authDetails, {
          onSuccess: (result) => {
            const idToken = result.getIdToken().getJwtToken();
            const accessToken = result.getAccessToken().getJwtToken();
            const refreshToken = result.getRefreshToken().getToken();
            // Clear any stale org selection from a previous session before storing new auth
            localStorage.removeItem("selectedOrganizationId");
            localStorage.removeItem("selectedOrganizationName");
            localStorage.setItem("idToken", idToken);
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            window.location.reload();
            resolve(result);
          },
          onFailure: (err) => reject(err),
          newPasswordRequired: () => {
            setCognitoUser(cognitoUserInstance);
            setNeedsPasswordChange(true);
            setLoading(false);
          },
        });
      });
    } catch (err) {
      const errorKey = err.code
        ? `loginPage.errors.${err.code.charAt(0).toLowerCase() + err.code.slice(1)}`
        : "loginPage.errors.generic";
      setError(t(errorKey, err.message || "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotClick = () => {
    setView("forgotPassword");
    setError("");
    setSuccessMessage("");
  };

  const handleCodeSent = (email) => {
    setResetEmail(email);
    setView("resetPassword");
  };

  const handleResetSuccess = () => {
    setView("login");
    setSuccessMessage(t("loginPage.forgotPasswordForm.resetSuccess"));
  };

  if (needsPasswordChange && cognitoUser) {
    return (
      <PasswordChangeForm
        cognitoUser={cognitoUser}
        onPasswordChanged={() => window.location.reload()}
        onCancel={() => setNeedsPasswordChange(false)}
      />
    );
  }

  return (
    <div className="h-[100dvh] w-full lg:grid lg:grid-cols-2 font-sans overflow-hidden bg-[#09090b]">
      {/* Left Panel: Visual/Brand Section */}
      <div className="relative hidden lg:flex flex-col items-center justify-center text-white p-12 overflow-hidden bg-[#09090b]">
        {/* Animated Background Elements */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary-600/20 blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-600/10 blur-[150px] animate-pulse delay-700"></div>

        {/* Subtle Pattern Overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        <div className="relative z-10 w-full max-w-lg">
          <div className="backdrop-blur-2xl bg-white/[0.03] border border-white/10 p-12 rounded-[3.5rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden relative group">
            {/* Inner Glow */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary-500/20 rounded-full blur-3xl group-hover:bg-primary-500/30 transition-all duration-700"></div>

            <div className="relative z-10 flex flex-col items-center">
              <div className="mb-10 p-8 bg-zinc-900/50 rounded-[2.5rem] shadow-2xl transform transition-transform duration-500 group-hover:scale-[1.02] border border-white/5">
                <img
                  src="/app_logo_dark.png"
                  alt="The Athletics Logo"
                  className="w-48 h-auto object-contain"
                />
              </div>
              <h1 className="text-5xl font-black text-white mb-4 tracking-tighter uppercase italic">
                THE ATHLETICS
              </h1>
              <p className="text-xl text-slate-400 font-medium text-center max-w-[280px] leading-relaxed">
                Gelişmiş Antreman & Performans Yönetim Sistemi
              </p>
            </div>

            {/* Bottom Accent */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-50"></div>
          </div>
        </div>
      </div>

      <div
        className={`h-full w-full flex flex-col items-center justify-center ${colors.bgPrimary} p-6 sm:p-12 relative overflow-y-auto overflow-x-hidden custom-scrollbar selection:bg-primary-500/30`}
      >
        {/* Mobile Background Elements - Enhanced Depth */}
        <div className="absolute top-[-10%] right-[-10%] w-[300px] h-[300px] bg-primary-600/10 blur-[100px] rounded-full pointer-events-none lg:hidden"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[300px] h-[300px] bg-violet-600/10 blur-[100px] rounded-full pointer-events-none lg:hidden"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary-900/5 via-transparent to-transparent pointer-events-none lg:hidden"></div>

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-10 lg:hidden">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-primary-500/20 blur-2xl rounded-full"></div>
              <div className="relative p-6 bg-zinc-950/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border border-white/10">
                <img
                  src="/app_logo_dark.png"
                  alt="Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
            </div>
          </div>

          {view === "login" && (
            <div className="animate-in fade-in slide-in-from-bottom-6 duration-500 w-full max-w-md my-auto">
              <div className="mb-10 text-center lg:text-left">
                <h2
                  className={`text-3xl lg:text-5xl font-black ${colors.textPrimary} mb-2 tracking-tight uppercase italic`}
                >
                  {t("loginPage.welcome")}
                </h2>
                <div className="h-1 w-12 bg-primary-500 mx-auto lg:ml-1 mb-4 rounded-full"></div>
                <p className={`${colors.textSecondary} text-base lg:text-lg font-semibold opacity-80`}>
                  {t("loginPage.signInMessage")}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className={`block text-sm font-bold ${colors.textSecondary} mb-2.5 ml-1`}
                  >
                    {t("loginPage.emailLabel")}
                  </label>
                  <div className="relative group">
                    <Mail
                      className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted} group-focus-within:text-primary-500 transition-colors`}
                    />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-4 rounded-2xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 transition-all duration-300 placeholder:text-slate-600 font-medium`}
                      placeholder={t("loginPage.emailPlaceholder")}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2.5 ml-1">
                    <label
                      htmlFor="password"
                      className={`block text-sm font-bold ${colors.textSecondary}`}
                    >
                      {t("loginPage.passwordLabel")}
                    </label>
                    <button
                      type="button"
                      onClick={handleForgotClick}
                      className="text-sm font-bold text-primary-400 hover:text-primary-300 transition-colors py-1 px-2 -mr-2 rounded-lg active:bg-primary-500/10"
                    >
                      {t("loginPage.forgotPassword")}
                    </button>
                  </div>
                  <div className="relative group">
                    <Lock
                      className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${colors.textMuted} group-focus-within:text-primary-500 transition-colors`}
                    />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-14 py-4 rounded-2xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 transition-all duration-300 placeholder:text-slate-600 font-medium`}
                      placeholder={t("loginPage.passwordPlaceholder")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-4 top-1/2 -translate-y-1/2 ${colors.textMuted} hover:${colors.textSecondary} transition-colors p-1`}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center space-x-3 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl animate-in shake duration-500">
                    <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
                    <span className="text-sm font-semibold text-rose-500">
                      {error}
                    </span>
                  </div>
                )}

                {successMessage && (
                  <div className="flex items-center space-x-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    <span className="text-sm font-semibold text-emerald-500">
                      {successMessage}
                    </span>
                  </div>
                )}

                <div className="flex items-center select-none ml-1">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500/40 bg-zinc-800 border-zinc-700 rounded-lg cursor-pointer transition-all"
                  />
                  <label
                    htmlFor="remember-me"
                    className={`ml-3 block text-sm font-bold ${colors.textSecondary} cursor-pointer`}
                  >
                    {t("loginPage.rememberMe", "Beni Hatırla")}
                  </label>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="relative w-full overflow-hidden group select-none touch-manipulation"
                  >
                    {/* Button Body */}
                    <div className={`
                        flex justify-center items-center py-4 px-4 rounded-2xl font-black text-lg tracking-wider uppercase
                        bg-gradient-to-br from-primary-500 to-primary-700 text-white
                        transition-all duration-200 ease-out
                        active:opacity-80 active:from-primary-600 active:to-primary-800
                        disabled:bg-zinc-800 disabled:from-zinc-800 disabled:to-zinc-900 disabled:text-zinc-600 disabled:cursor-not-allowed
                        shadow-[0_10px_40px_-10px_rgba(37,99,235,0.5)]
                      `}>
                      {loading ? (
                        <div className="flex items-center space-x-3">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span className="font-bold">{t("loginPage.signingIn")}</span>
                        </div>
                      ) : (
                        <span className="relative z-10">{t("loginPage.signIn")}</span>
                      )}
                    </div>

                    {/* Glass Shine Effect */}
                    <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none"></div>
                  </button>
                </div>
              </form>
            </div>
          )}

          {view === "forgotPassword" && (
            <div className="animate-in fade-in slide-in-from-right-6 duration-500">
              <ForgotPasswordForm
                authService={authService}
                onCodeSent={handleCodeSent}
                onCancel={() => setView("login")}
              />
            </div>
          )}

          {view === "resetPassword" && (
            <div className="animate-in fade-in slide-in-from-right-6 duration-500">
              <ResetPasswordForm
                email={resetEmail}
                authService={authService}
                onResetSuccess={handleResetSuccess}
                onCancel={() => setView("login")}
              />
            </div>
          )}

          <div
            className={`mt-12 text-center ${colors.textMuted} text-xs font-bold tracking-widest uppercase opacity-40`}
          >
            © {new Date().getFullYear()} The Athletics. Legacy of Excellence.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
